/**
 * Pure geometry for stereo 3D output (no PlayCanvas imports, so it is unit-testable).
 *
 * Formats, as a display expects them:
 *
 *   - 'sbs'       Half side-by-side. Each eye is squeezed into half the width; a 3D TV or
 *                 3D projector in side-by-side mode stretches each half back to full width.
 *   - 'tb'        Half top-and-bottom (over-under). Left eye on top, each squeezed to half
 *                 the height; the display stretches each half back to full height.
 *   - 'sbs-full'  Full side-by-side: two complete, unsqueezed frames side by side (letterboxed
 *                 on a 16:9 screen), as in "full SBS" video. For parallel / cross-eyed free
 *                 viewing, stereo monitors that take a full-width pair, and phone viewers.
 *   - 'anaglyph'  Red/cyan glasses on any ordinary screen (Dubois colour matrices).
 *
 * Each eye's image is the mono frame scaled onto its region of the screen, so the 2D interface
 * can be drawn per eye with the same transform (see stereoUi.ts). Every eye is rendered with
 * SQUARE pixels into an offscreen target and only then squeezed on the way to the screen. The engine's gsplat footprint maths uses one focal length for
 * both axes (`focal = viewport_size.x * projMat00` in gsplatCorner), so drawing a squeezed
 * eye directly would flatten every splat to half its height.
 */

import type { StereoFormat } from '../types';

export type { StereoFormat };

export const STEREO_FORMATS: readonly StereoFormat[] = ['anaglyph', 'sbs', 'tb', 'sbs-full'];

export function isStereoFormat(value: string | null | undefined): value is StereoFormat {
    return typeof value === 'string' && (STEREO_FORMATS as readonly string[]).includes(value);
}

/** Formats that split the screen into one region per eye. */
export type SplitStereoFormat = Exclude<StereoFormat, 'anaglyph'>;

export function isSplitStereoFormat(format: StereoFormat): format is SplitStereoFormat {
    return format !== 'anaglyph';
}

/**
 * Parse a `?stereo=` value. Accepts the format names plus common aliases a person might type
 * on a TV remote or in a kiosk URL. Returns null for anything else (including "off").
 */
export function parseStereoParam(value: string | null | undefined): StereoFormat | null {
    if (!value) return null;
    const v = value.trim().toLowerCase();
    if (isStereoFormat(v)) return v;
    switch (v) {
        case 'red-cyan':
        case 'redcyan':
        case 'anaglyph-red-cyan':
            return 'anaglyph';
        case 'side-by-side':
        case 'sidebyside':
        case 'half-sbs':
        case 'hsbs':
            return 'sbs';
        case 'top-bottom':
        case 'topbottom':
        case 'over-under':
        case 'ou':
        case 'half-tb':
        case 'htb':
            return 'tb';
        case 'full-sbs':
        case 'fsbs':
            return 'sbs-full';
        default:
            return null;
    }
}

/** Viewport in render-target pixels, GL convention (x from the left, y from the BOTTOM). */
export interface EyeViewport {
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface StereoLayout {
    /** Offscreen render target size holding both eyes. */
    targetWidth: number;
    targetHeight: number;
    /** Size of each eye's region (square pixels). */
    eyeWidth: number;
    eyeHeight: number;
    /** Aspect ratio each eye is rendered at (width / height). */
    eyeAspect: number;
    /** [left eye, right eye] regions inside the target, before any eye swap. */
    viewports: [EyeViewport, EyeViewport];
    /**
     * Where the target is drawn on screen, as fractions of the canvas (GL convention, y from the
     * bottom). Full side-by-side is letterboxed into the middle half; the rest fill the screen.
     */
    display: { x: number; y: number; width: number; height: number };
}

/**
 * Lay both eyes out in the offscreen target so that a plain stretched copy of the target to
 * `display` yields the format (sbs / tb / sbs-full), or so the anaglyph pass can sample each
 * eye at the same screen position (eyes side by side, each at full screen size). Every eye
 * keeps the canvas aspect ratio, i.e. shows exactly the mono frame.
 *
 * `maxTextureSize` keeps the target inside the device limit by shrinking both eyes evenly.
 */
export function computeStereoLayout(
    format: StereoFormat,
    canvasWidth: number,
    canvasHeight: number,
    maxTextureSize = 8192,
): StereoLayout {
    const w = Math.max(1, Math.floor(canvasWidth));
    const h = Math.max(1, Math.floor(canvasHeight));

    // Eye size before the texture-limit clamp, and how many eyes across / down the target holds.
    let eyeW: number;
    let eyeH: number;
    let across: number;
    let down: number;
    let display = { x: 0, y: 0, width: 1, height: 1 };
    switch (format) {
        case 'sbs-full':
            // Half the width at the canvas aspect: the pair fills the middle half of the height.
            eyeW = Math.max(1, Math.floor(w / 2));
            eyeH = Math.max(1, Math.round((eyeW * h) / w));
            across = 2;
            down = 1;
            display = { x: 0, y: 0.25, width: 1, height: 0.5 };
            break;
        case 'tb':
            eyeW = w;
            eyeH = h;
            across = 1;
            down = 2;
            break;
        case 'sbs':
        case 'anaglyph':
        default:
            eyeW = w;
            eyeH = h;
            across = 2;
            down = 1;
            break;
    }

    const eyeAspect = w / h;
    const maxSize = Math.max(2, Math.floor(maxTextureSize));
    const scale = Math.min(1, maxSize / (eyeW * across), maxSize / (eyeH * down));
    if (scale < 1) {
        eyeW = Math.max(1, Math.floor(eyeW * scale));
        eyeH = Math.max(1, Math.floor(eyeH * scale));
    }

    const left: EyeViewport = { x: 0, y: 0, width: eyeW, height: eyeH };
    const right: EyeViewport = { x: 0, y: 0, width: eyeW, height: eyeH };
    if (down === 2) {
        // Top-and-bottom: the left eye goes on TOP. GL viewports count y from the bottom.
        left.y = eyeH;
    } else {
        right.x = eyeW;
    }

    return {
        targetWidth: eyeW * across,
        targetHeight: eyeH * down,
        eyeWidth: eyeW,
        eyeHeight: eyeH,
        eyeAspect,
        viewports: [left, right],
        display,
    };
}

export interface FrustumBounds {
    left: number;
    right: number;
    bottom: number;
    top: number;
}

/**
 * Off-axis (asymmetric) frustum for one eye, at the near plane.
 *
 * The eye sits at `eyeOffset` along the camera's right axis (negative = left eye). Its frustum
 * is sheared so that, at `convergence` distance, both eyes see exactly the window the mono
 * camera would see there. Objects at that distance land at the screen surface with zero
 * parallax; nearer ones come out of the screen, farther ones sit behind it. Toe-in (rotating
 * each eye) would add vertical parallax at the edges, which is what makes stereo tiring.
 */
export function computeEyeFrustum(
    fovDegrees: number,
    horizontalFov: boolean,
    aspect: number,
    near: number,
    eyeOffset: number,
    convergence: number,
): FrustumBounds {
    const tanHalf = Math.tan((fovDegrees * Math.PI) / 360);
    let halfHeight: number;
    let halfWidth: number;
    if (horizontalFov) {
        halfWidth = near * tanHalf;
        halfHeight = halfWidth / aspect;
    } else {
        halfHeight = near * tanHalf;
        halfWidth = halfHeight * aspect;
    }
    const shift = convergence > 0 ? (-eyeOffset * near) / convergence : 0;
    return {
        left: -halfWidth + shift,
        right: halfWidth + shift,
        bottom: -halfHeight,
        top: halfHeight,
    };
}

/**
 * Map a pointer position on the canvas (CSS pixels, y down) to the point of the MONO frame it
 * looks at, so clicks and hovers can keep using the ordinary camera for ray casts.
 *
 * In the split formats each half of the screen shows a whole eye image, so a click in either
 * half is folded onto the full frame. The small horizontal offset between the eye and the mono
 * camera is ignored: it is zero at the screen distance and a few pixels elsewhere.
 */
export function mapStereoPointerToMono(
    format: StereoFormat,
    x: number,
    y: number,
    width: number,
    height: number,
): { x: number; y: number } {
    if (width <= 0 || height <= 0) return { x, y };
    switch (format) {
        case 'sbs': {
            const half = width / 2;
            const lx = x >= half ? x - half : x;
            return { x: (lx / half) * width, y };
        }
        case 'tb': {
            const half = height / 2;
            const ly = y >= half ? y - half : y;
            return { x, y: (ly / half) * height };
        }
        case 'sbs-full': {
            // Each eye is the whole frame at half size, in the middle half of the height.
            const half = width / 2;
            const lx = x >= half ? x - half : x;
            return { x: lx * 2, y: (y - height / 4) * 2 };
        }
        case 'anaglyph':
        default:
            return { x, y };
    }
}

/**
 * Distance between the eyes, in scene units. `depth` 1 follows the stereographers' 1/30 rule
 * (interaxial = screen distance / 30), which keeps the background's on-screen separation well
 * under human eye spacing even on a projector wall, so eyes never have to diverge.
 */
export function computeEyeSeparation(screenDistance: number, depth: number): number {
    if (!(screenDistance > 0) || !(depth > 0)) return 0;
    return (screenDistance * depth) / 30;
}

export const STEREO_DEPTH_MIN = 0;
export const STEREO_DEPTH_MAX = 2;
export const STEREO_DEPTH_DEFAULT = 1;

export function clampStereoDepth(depth: number | null | undefined): number {
    const n = typeof depth === 'number' && Number.isFinite(depth) ? depth : STEREO_DEPTH_DEFAULT;
    return Math.min(STEREO_DEPTH_MAX, Math.max(STEREO_DEPTH_MIN, n));
}

const METERS_PER_UNIT: Record<string, number> = {
    meters: 1,
    centimeters: 0.01,
    feet: 0.3048,
    inches: 0.0254,
};

/** Comfortable "screen" distance for a person-scale view, used in calibrated scenes. */
const HUMAN_SCREEN_DISTANCE_METERS = 2.5;
const FALLBACK_SCREEN_DISTANCE = 3;

/**
 * Automatic zero-parallax distance, in scene units, when the creator hasn't set one:
 *
 *   1. While orbiting, the orbit focus: whatever the visitor is circling sits at the screen
 *      surface, and the depth scales as they zoom.
 *   2. In a scene calibrated for measurements, 2.5 m, so a walkthrough has human-scale depth.
 *      The default calibration (1 scene unit = 1 m) is the editor's "not set", so it is skipped.
 *   3. The creator's saved orbit view distance, a scale reference chosen for this scene.
 *   4. 3 scene units.
 */
export function resolveAutoScreenDistance(input: {
    orbitFocusDistance?: number | null;
    sceneScale?: number | null;
    sceneScaleUnit?: string | null;
    savedOrbitDistance?: number | null;
}): number {
    const positive = (v: number | null | undefined): v is number => typeof v === 'number' && Number.isFinite(v) && v > 0;
    if (positive(input.orbitFocusDistance)) return input.orbitFocusDistance;
    if (positive(input.sceneScale)) {
        const metersPerUnit = input.sceneScale * (METERS_PER_UNIT[input.sceneScaleUnit ?? 'meters'] ?? 1);
        if (metersPerUnit > 0 && metersPerUnit !== 1) return HUMAN_SCREEN_DISTANCE_METERS / metersPerUnit;
    }
    if (positive(input.savedOrbitDistance)) return input.savedOrbitDistance;
    return FALLBACK_SCREEN_DISTANCE;
}
